import { postConstruct } from 'inversify';
import { v4 as uuid } from 'uuid';
import { nextTick } from 'vue';
import {
  createToastInterface, EventBus, PluginOptions, ToastInterface,
} from 'vue-toastification';

import Toast from './Toast.vue';
import type {
  IToast,
  IToastContexts,
  IToastOptions,
  IToaster,
  ToastContent,
  ToastId,
  IToastContextValue,

} from './types';

import { Ref } from '@/decorators/ref';
import { service } from '@/decorators/service';
import { TOAST_CONTEXT, TOAST_TYPE } from '@/services/toaster/enum';

export const defaultContexts: IToastContexts = new Map(Object.entries({
  [TOAST_CONTEXT.ERROR]: 'Error',
  [TOAST_CONTEXT.INFO]: 'Info',
  [TOAST_CONTEXT.CONNECTION]: 'Connection',
  [TOAST_CONTEXT.SUCCESS]: 'Success',
  [TOAST_CONTEXT.WARNING]: 'Warning',
}));

export const defaultOptions = {
};

export const ToasterPluginOptionsSymbol = Symbol.for('ToasterPluginOptions');

const cleanString = (value?: string): string => (value ? value.replace(/_+/g, ' ') : '');

const toString = (value?: string | (() => string)): string => `${typeof value === 'function' ? value() : cleanString(value)}`;

@service(Toaster)
export class Toaster implements IToaster {
  // Refs.
  @Ref readonly active: Map<ToastId, IToast> = new Map();
  @Ref readonly contexts: IToastContexts = new Map(defaultContexts);

  // Properties.
  private toastInterface: ToastInterface;

  // Methods.
  @postConstruct()
  initialize() {
    this.toastInterface = createToastInterface(<PluginOptions> {
      closeOnClick: false,
      toastDefaults: {
        connection: {
          icon: {
            iconTag: 'span',
            iconClass: 'network-offline-icon',
          },
        },
        error: {
          icon: {
            iconTag: 'span',
            iconClass: 'fas fa-fw fa-exclamation-triangle',
          },
        },
        info: {
          icon: {
            iconTag: 'span',
            iconClass: 'fas fa-fw fa-info-circle',
          },
        },
        success: {
          icon: {
            iconTag: 'span',
            iconClass: 'fas fa-fw fa-check-circle',
          },
        },
        warning: {
          icon: {
            iconTag: 'span',
            iconClass: 'fas fa-fw fa-exclamation-square',
          },
        },
      },
      timeout: false,
      toastClassName: 'zo-toast',
      eventBus: new EventBus(),
    });

    // Must use nextTick here because vue-toastification uses it to mount a separate app interface/event bus.
    // eslint-disable-next-line no-void
    void nextTick();
  }

  protected buildToast(
    type: TOAST_TYPE | string,
    content?: ToastContent,
    options?: Partial<IToastOptions>,
  ): Readonly<IToast> {
    const id = options?.id || uuid();

    const opts: IToastOptions = <IToastOptions> { ...options, id, type };

    // Keep an original copy of the toast; necessary for dynamic rendering (i.e. real-time translations).
    if (opts.original === undefined) {
      opts.original = Object.freeze({ ...opts, content });
    }

    const autoDismiss = Number(opts?.autoDismiss ?? (type === TOAST_TYPE.SUCCESS ? 4500 : 0));
    if (autoDismiss) {
      const wordTimeout = `${content}`.split(' ').length * 300;
      opts.timeout = Math.max(autoDismiss, wordTimeout);
    }
    if (!opts?.timeout) {
      opts.timeout = false;
    }

    opts.onClose = () => {
      this.active.delete(opts.id);
      if (typeof opts.original.onClose === 'function') {
        opts.original.onClose();
      }
    };

    const context = toString(opts.original.context);
    opts.context = toString(this.contexts.get(context) ?? context);

    return Object.freeze({
      id,
      content: toString(opts.original.content),
      options: opts,
    });
  }

  connection(content?: ToastContent, options?: Partial<IToastOptions>): IToast {
    return this.create(TOAST_TYPE.CONNECTION, content, {
      // Connection toasts should be handled manually by default.
      closeButton: false,
      context: TOAST_CONTEXT.CONNECTION,
      ...options,
    });
  }

  create(type: TOAST_TYPE | string, content?: ToastContent, options?: Partial<IToastOptions>): Readonly<IToast> {
    const toast = this.buildToast(type, content, options);

    this.toastInterface({ component: Toast, props: toast }, toast.options);

    this.active.set(toast.id, toast);

    return toast;
  }

  dismiss(toast?: IToast | ToastId) {
    const id = typeof toast === 'string' ? toast : toast?.id;
    if (id) {
      this.toastInterface.dismiss(id);
    }
  }

  dismissAll() {
    this.toastInterface.clear();
  }

  error(content?: ToastContent | Error, options?: Partial<IToastOptions>): Readonly<IToast> {
    const error = content instanceof Error ? content : undefined;
    return this.create(TOAST_TYPE.ERROR, error?.message ?? content as ToastContent, {
      context: TOAST_CONTEXT.ERROR,
      ...options,
      error,
    });
  }

  info(content?: ToastContent, options?: Partial<IToastOptions>): Readonly<IToast> {
    return this.create(TOAST_TYPE.INFO, content, {
      context: TOAST_CONTEXT.INFO,
      ...options,
    });
  }

  refresh() {
    this.active.forEach((toast) => {
      const updatedToast = this.buildToast(toast.options.type, toast.content, toast.options);

      this.active.set(updatedToast.id, updatedToast);

      this.update(updatedToast);
    });
  }

  setContexts(contexts?: Record<TOAST_CONTEXT | string, IToastContextValue>) {
    Object.entries({ ...Object.fromEntries(defaultContexts.entries()), ...contexts }).forEach(([value, label]) => {
      this.contexts.set(value, label);
    });

    return this;
  }

  success(content?: ToastContent, options?: Partial<IToastOptions>): Readonly<IToast> {
    return this.create(TOAST_TYPE.SUCCESS, content, {
      context: TOAST_CONTEXT.SUCCESS,
      ...options,
    });
  }

  update(toast: IToast, create?: false) {
    this.toastInterface.update(toast.id, {
      ...toast,
      content: { component: Toast, props: toast },
    }, create);
  }

  warning(content?: ToastContent, options?: Partial<IToastOptions>): Readonly<IToast> {
    return this.create(TOAST_TYPE.WARNING, content, {
      context: TOAST_CONTEXT.WARNING,
      ...options,
    });
  }
}
